export interface CSSVarType {
  [key: string]: string;
}

export const mainColors = {
  red: '#ff0000',
  white: '#ffffff',
  black: '#000000',
  gray: '#808080',
  light_gray: '#c0c0c0',
  very_light_gray: '#f0f0f0',

  // stirista specific colors
  accent_rollover: '#24544e',
  buttons: '#32887d',
  active_state_page: '#ff8040',
  secondary_buttons_accent: '#ff6a2e',
  text: '#6d6e70',
  background: '#f2f2f2',
};

// spyglass specific colors
export const lightTheme: CSSVarType = {
  // Brand palette (approved)
  '--ap-color-primary': '#32887d',
  '--ap-color-secondary': '#24544e',
  '--ap-color-cta': '#ff8040',
  '--ap-color-cta-accent': '#ff6a2e',
  // Approved accents (do not apply on light surfaces in this commit)
  '--ap-color-accent-1': '#9cd3cc',
  '--ap-color-accent-2': '#d9f2ee',
  // Gradients (token-only usage)
  '--ap-gradient-brand':
    'linear-gradient(135deg, var(--ap-color-primary) 0%, var(--ap-color-secondary) 100%)',
  '--ap-gradient-cta':
    'linear-gradient(135deg, var(--ap-color-cta) 0%, var(--ap-color-cta-accent) 100%)',

  // Semantic tokens (keep color usage intentional)
  // - Orange: CTA/action only (buttons + tiny indicators)
  // - Teal: subtle tints/selection/focus ring (no large gradient washes on content surfaces)
  '--ap-border-default': 'var(--disabled)',
  '--ap-border-subtle': '#0000001a', // 10% black
  '--ap-selection-bg': '#32887d14', // 8% primary
  '--ap-selection-border': '#32887d47', // 28% primary
  '--ap-focus-ring': '#32887d8c', // 55% primary

  // Status colors (brand-tonal)
  '--ap-status-success-text': 'var(--ap-color-secondary)',
  '--ap-status-success-bg': '#32887d24', // 14% primary
  '--ap-status-success-border': '#32887d47', // 28% primary
  '--ap-status-info-text': 'var(--ap-color-secondary)',
  '--ap-status-info-bg': '#32887d1a', // 10% primary
  '--ap-status-info-border': '#32887d38', // 22% primary
  '--ap-status-neutral-text': 'var(--text-muted)',
  '--ap-status-neutral-bg': '#94a3b838', // 22% slate
  '--ap-status-neutral-border': '#94a3b84d', // 30% slate
  '--ap-status-warn-text': '#7c2d12',
  '--ap-status-warn-bg': '#ff804029', // 16% cta
  '--ap-status-warn-border': '#ff804047', // 28% cta
  '--ap-status-danger-text': '#7f1d1d',
  '--ap-status-danger-bg': '#d9534f24', // 14% danger
  '--ap-status-danger-border': '#d9534f42', // 26% danger

  // Sidebar (dark teal background with light text)
  '--ap-sidebar-bg': 'var(--ap-color-secondary)',
  '--ap-sidebar-gradient-overlay': '#ffffff0f', // 6% white
  '--ap-sidebar-shadow': '#24544e2e', // 18% secondary
  '--ap-sidebar-text': '#ffffffeb', // 92% white
  '--ap-sidebar-logo-bg': '#ffffff14', // 8% white
  '--ap-sidebar-logo-border': '#9cd4cc38', // 22% accent-1
  '--ap-sidebar-divider': '#ffffff1f', // 12% white
  '--ap-sidebar-hover-bg': '#9cd4cc29', // 16% accent-1
  '--ap-sidebar-hover-border': '#9cd4cc2e', // 18% accent-1
  '--ap-sidebar-active-bg': '#9cd4cc2e', // 18% accent-1
  '--ap-sidebar-active-border': '#9cd4cc38', // 22% accent-1
  '--ap-sidebar-active-indicator': 'var(--ap-color-accent-1)',
  '--ap-sidebar-admin-text': '#ffc56f',
  '--ap-sidebar-admin-bg': '#ffc56f24', // 14% admin
  '--ap-sidebar-admin-border': '#ffc56f2e', // 18% admin
  '--ap-sidebar-admin-indicator': '#ffc56f',

  // App component tokens (backed by approved palette)
  '--button-primary': 'var(--ap-color-primary)',
  '--button-primary-accent': 'var(--ap-color-secondary)',
  '--accent-rgb': '50, 136, 125',
  '--button-secondary': 'var(--ap-color-cta)',
  '--button-secondary-accent': 'var(--ap-color-cta-accent)',
  '--button-secondary-text': '#ffffff',
  '--disabled': '#dcdcdc',
  '--disabled-40-opacity': '#dcdcdc66', // 40% disabled
  '--toggle-on': 'var(--ap-color-cta)',
  '--toggle-off': '#6d6e70',
  '--text-primary': '#6d6e70',
  '--text-secondary': '#2a2a2a',
  '--text-muted': '#9e9e9e',
  '--page-background': '#f2f2f2',
  '--form-background': '#ffffff',
  '--modal-background': '#ffffff',
  '--accent-background': 'var(--ap-selection-bg)',
  '--accent-text': 'var(--ap-color-secondary)',
  // NOTE: `--focus-border` is currently used as a background in some places (e.g. sidebar active state),
  // so keep it as a subtle tint rather than a saturated teal.
  '--focus-border': 'var(--ap-selection-bg)',
  '--focus-background': '#32887d1f', // 12% primary
  '--focus-text': 'var(--ap-color-secondary)',
  '--gradient-1-light': '#fed7aa',
  '--gradient-1-light-40-opacity': '#fed7aa66', // 40% gradient-1-light
  '--gradient-1-heavy': '#fdba74',
  // Used by some non-CTA borders (e.g. dashboard checklist card). Keep it teal-subtle, not orange.
  '--active-indicator': 'var(--ap-selection-border)',
  '--error-danger': '#d9534f',
  '--error-danger-40-opacity': '#fc9d9d66', // 40% error tint
  '--node-label': '#1e293b',
  '--reduced-opacity-background': '#fff7ed4d',
  '--link-color': 'var(--ap-color-primary)',
  '--link-color-20-opacity': '#32887d33', // 20% primary
  '--active-status-color': '#daefec',
  '--draft-status-color': '#f2f2f2',
  '--archived-status-color': '#fdefd6',
  '--olive-text': '#7d6a3a',
  '--overlay-background': '#ffffff1a', // 10% white
  '--overlay-shadow': '#0000001a', // 10% black
  // Help-center panel tokens
  '--ap-panel-bg': '#ffffffe6', // rgba(255, 255, 255, 0.9)
  '--ap-panel-bg-solid': '#ffffffeb', // rgba(255, 255, 255, 0.92)
  '--ap-panel-bg-muted': '#ffffffdb', // rgba(255, 255, 255, 0.86)
  '--ap-panel-bg-subtle': '#ffffffd1', // rgba(255, 255, 255, 0.82)
  '--ap-panel-border': '#24544E1F', // rgba(36, 84, 78, 0.12)
  '--ap-panel-border-light': '#24544E14', // rgba(36, 84, 78, 0.08)
  '--ap-panel-border-hover': '#9CD4CCB3', // rgba(156, 212, 204, 0.7)
  '--ap-panel-shadow-heavy': '#24544E73', // rgba(36, 84, 78, 0.45)
  '--ap-panel-shadow-light': '#24544E38', // rgba(36, 84, 78, 0.22)

  '--ap-accent-bg-80': '#D9F2EECC', // rgba(217, 242, 238, 0.8)
  '--ap-accent-bg-50': '#D9F2EE80', // rgba(217, 242, 238, 0.5)
  '--ap-accent-bg-30': '#D9F2EE4D', // rgba(217, 242, 238, 0.3)
  '--ap-accent-bg-20': '#D9F2EE33', // rgba(217, 242, 238, 0.2)
  '--ap-accent-bg-15': '#D9F2EE26', // rgba(217, 242, 238, 0.15)

  '--ap-accent-border': '#9CD3CC8C', // rgba(156, 211, 204, 0.55)
  '--ap-accent-border-light': '#9CD3CC59', // rgba(156, 211, 204, 0.35)
  '--ap-accent-border-hover': '#9CD3CCBF', // rgba(156, 211, 204, 0.75)

  '--ap-warn-bg': '#FFEDD3B3', // rgba(255, 237, 211, 0.7)
  '--ap-warn-border': '#FFC05C8C', // rgba(255, 192, 92, 0.55)
  '--ap-warn-text': '#d97706',
  // Table specific branding
  '--tb-hover': '#1a3f3a',
};
export const darkTheme: CSSVarType = {
  // Brand palette (approved)
  '--ap-color-primary': '#32887d',
  '--ap-color-secondary': '#24544e',
  '--ap-color-cta': '#ff8040',
  '--ap-color-cta-accent': '#ff6a2e',
  // Approved accents
  '--ap-color-accent-1': '#9cd3cc',
  '--ap-color-accent-2': '#d9f2ee',
  // Gradients (token-only usage)
  '--ap-gradient-brand':
    'linear-gradient(135deg, var(--ap-color-primary) 0%, var(--ap-color-secondary) 100%)',
  '--ap-gradient-cta':
    'linear-gradient(135deg, var(--ap-color-cta) 0%, var(--ap-color-cta-accent) 100%)',

  // Semantic tokens (keep color usage intentional)
  '--ap-border-default': 'var(--disabled)',
  '--ap-border-subtle': '#ffffff24', // 14% white
  '--ap-selection-bg': '#32887d2e', // 18% primary
  '--ap-selection-border': '#32887d61', // 38% primary
  '--ap-focus-ring': '#32887db3', // 70% primary

  // Status colors (brand-tonal)
  '--ap-status-success-text': 'var(--ap-color-primary)',
  '--ap-status-success-bg': '#32887d33', // 20% primary
  '--ap-status-success-border': '#32887d5c', // 36% primary
  '--ap-status-info-text': 'var(--ap-color-primary)',
  '--ap-status-info-bg': '#32887d29', // 16% primary
  '--ap-status-info-border': '#32887d47', // 28% primary
  '--ap-status-neutral-text': 'var(--text-muted)',
  '--ap-status-neutral-bg': '#94a3b847', // 28% slate
  '--ap-status-neutral-border': '#94a3b861', // 38% slate
  '--ap-status-warn-text': '#fed7aa',
  '--ap-status-warn-bg': '#ff804038', // 22% cta
  '--ap-status-warn-border': '#ff804052', // 32% cta
  '--ap-status-danger-text': '#fca5a5',
  '--ap-status-danger-bg': '#d9534f33', // 20% danger
  '--ap-status-danger-border': '#d9534f52', // 32% danger

  // Sidebar (dark teal background with light text)
  '--ap-sidebar-bg': 'var(--ap-color-secondary)',
  '--ap-sidebar-gradient-overlay': '#ffffff0f', // 6% white
  '--ap-sidebar-shadow': '#24544e2e', // 18% secondary
  '--ap-sidebar-text': '#ffffffeb', // 92% white
  '--ap-sidebar-logo-bg': '#ffffff14', // 8% white
  '--ap-sidebar-logo-border': '#9cd4cc38', // 22% accent-1
  '--ap-sidebar-divider': '#ffffff1f', // 12% white
  '--ap-sidebar-hover-bg': '#9cd4cc29', // 16% accent-1
  '--ap-sidebar-hover-border': '#9cd4cc2e', // 18% accent-1
  '--ap-sidebar-active-bg': '#9cd4cc2e', // 18% accent-1
  '--ap-sidebar-active-border': '#9cd4cc38', // 22% accent-1
  '--ap-sidebar-active-indicator': 'var(--ap-color-accent-1)',
  '--ap-sidebar-admin-text': '#ffc56f',
  '--ap-sidebar-admin-bg': '#ffc56f24', // 14% admin
  '--ap-sidebar-admin-border': '#ffc56f2e', // 18% admin
  '--ap-sidebar-admin-indicator': '#ffc56f',

  // App component tokens (backed by approved palette)
  '--button-primary': 'var(--ap-color-primary)',
  '--button-primary-accent': 'var(--ap-color-secondary)',
  '--accent-rgb': '50, 136, 125',
  '--button-secondary': 'var(--ap-color-cta)',
  '--button-secondary-accent': 'var(--ap-color-cta-accent)',
  '--button-secondary-text': '#ffffff',
  '--disabled': '#555555',
  '--disabled-40-opacity': '#dcdcdc66', // 40% disabled
  '--toggle-on': 'var(--ap-color-cta)',
  '--toggle-off': '#9e9e9e',
  '--text-primary': '#e0e0e0',
  '--text-secondary': '#ffffff',
  '--text-muted': '#a1a1a1',
  '--page-background': '#a1a1a1',
  '--form-background': '#2a2a2a',
  '--accent-background': 'var(--ap-selection-bg)',
  '--accent-text': 'var(--ap-color-primary)',
  '--modal-background': '#292929',
  '--focus-border': 'var(--ap-selection-bg)',
  '--focus-background': '#32887d33', // 20% primary
  '--focus-text': 'var(--ap-color-primary)',
  '--gradient-1-light': '#fed7aa',
  '--gradient-1-light-40-opacity': '#fed7aa66', // 40% gradient-1-light
  '--gradient-1-heavy': '#fdba74',
  '--active-indicator': 'var(--ap-selection-border)',
  '--error-danger': '#ff6b6b',
  '--node-label': '#ffffff',
  '--reduced-opacity-background': '#4a4a4a80',
  '--link-color': 'var(--ap-color-primary)',
  '--link-color-20-opacity': '#32887d33', // 20% primary
  '--active-status-color': '#daefec',
  '--draft-status-color': '#f2f2f2',
  '--archived-status-color': '#fdefd6',
  '--olive-text': '#7d6a3a',
  '--overlay-background': '#00000033', // 20% black
  '--overlay-shadow': '#0000004d', // 30% black
  // Help-center panel tokens (dark mode)
  '--ap-panel-bg': '#2A2A2AE6', // rgba(42, 42, 42, 0.9)
  '--ap-panel-bg-solid': '#2A2A2AEB', // rgba(42, 42, 42, 0.92)
  '--ap-panel-bg-muted': '#2A2A2ADB', // rgba(42, 42, 42, 0.86)
  '--ap-panel-bg-subtle': '#2A2A2AD1', // rgba(42, 42, 42, 0.82)

  '--ap-panel-border': '#9CD3CC26', // rgba(156, 211, 204, 0.15)
  '--ap-panel-border-light': '#9CD3CC14', // rgba(156, 211, 204, 0.08)
  '--ap-panel-border-hover': '#9CD4CC66', // rgba(156, 212, 204, 0.4)

  '--ap-panel-shadow-heavy': '#00000073', // rgba(0, 0, 0, 0.45)
  '--ap-panel-shadow-light': '#00000038', // rgba(0, 0, 0, 0.22)

  '--ap-accent-bg-80': '#24544E66', // rgba(36, 84, 78, 0.4)
  '--ap-accent-bg-50': '#24544E40', // rgba(36, 84, 78, 0.25)
  '--ap-accent-bg-30': '#24544E26', // rgba(36, 84, 78, 0.15)
  '--ap-accent-bg-20': '#24544E1A', // rgba(36, 84, 78, 0.1)
  '--ap-accent-bg-15': '#24544E14', // rgba(36, 84, 78, 0.08)

  '--ap-accent-border': '#9CD3CC4D', // rgba(156, 211, 204, 0.3)
  '--ap-accent-border-light': '#9CD3CC33', // rgba(156, 211, 204, 0.2)
  '--ap-accent-border-hover': '#9CD3CC80', // rgba(156, 211, 204, 0.5)

  '--ap-warn-bg': '#FFEDD326', // rgba(255, 237, 211, 0.15)
  '--ap-warn-border': '#FFC05C4D', // rgba(255, 192, 92, 0.3)
  '--ap-warn-text': '#fdba74',
  // Table specific branding
  '--tb-hover': '#1a3f3a',
};
